import { FullCourse } from 'server/types';
import { AsyncForm, FormSubSection, InputLabel, Subtext } from '@components/async-form';
import { rpx } from 'client/lib/rpx-client';
import { BtnPrimary, BtnSecondary } from '@components/buttons';
import { showToast } from '@components/toaster';
import { HeadingSecondary } from '@course/components/headings';
import { useAsyncState } from 'client/lib/hooks';
import { Spinner } from '@components/spinner';
import { useEffect, useRef, useState } from 'preact/hooks';
import { Toggle } from '@components/toggle';
import { UserProfileIcon } from '@components/avatars';
import { showDialog } from '@components/dialog';
import { showError } from '@components/app-error';

const store = rpx.courseSettings;

type Props = {
  course: FullCourse;
};

type HostCandidate = {
  id: UUID;
  email: string;
  name?: string;
  displayName?: string;
  profilePhotoUrl?: string;
  level: 'guide' | 'facilitator';
};

export function CalendarInviteSettings({ course }: Props) {
  const [settings, setSettings] = useAsyncState(
    () => store.getCalendarInviteSettings({ courseId: course.id }),
    [course.id],
  );

  const [enabled, setEnabled] = useState<boolean | undefined>(undefined);
  const [meetingUrl, setMeetingUrl] = useState<string | undefined>(undefined);
  const [memberDesc, setMemberDesc] = useState<string | undefined>(undefined);
  const [hostDesc, setHostDesc] = useState<string | undefined>(undefined);
  const [hostBuffer, setHostBuffer] = useState<string | undefined>(undefined);
  const [reminderMins, setReminderMins] = useState<string | undefined>(undefined);
  const [hostSelectionMode, setHostSelectionMode] = useState<'all' | 'explicit' | undefined>(
    undefined,
  );
  const [explicitHostIds, setExplicitHostIds] = useState<UUID[] | undefined>(undefined);
  const [validationError, setValidationError] = useState<string | undefined>(undefined);

  if (!settings) {
    return (
      <div class="flex justify-center">
        <Spinner class="border-sky-500 w-8 h-8" />
      </div>
    );
  }

  const currentEnabled = enabled ?? settings.calendarInvitesEnabled;
  const currentMeetingUrl = meetingUrl ?? settings.meetingUrlForInvites;
  const currentMemberDesc = memberDesc ?? settings.memberInviteDescription;
  const currentHostDesc = hostDesc ?? settings.hostInviteDescription;
  const currentHostBuffer = hostBuffer ?? String(settings.hostBufferMinutes);
  const currentReminderMins = reminderMins ?? String(settings.reminderMinutesBefore);

  const candidates = settings.hostCandidates as HostCandidate[];
  const settingsHostIds = settings.hostUserIds;
  const currentHostMode =
    hostSelectionMode ?? (settingsHostIds && settingsHostIds.length > 0 ? 'explicit' : 'all');
  const currentHostIds = explicitHostIds ?? settingsHostIds ?? [];

  function toggleHost(userId: UUID) {
    const current = currentHostIds;
    const next = current.includes(userId)
      ? current.filter((id) => id !== userId)
      : [...current, userId];
    setExplicitHostIds(next);
  }

  async function submit() {
    const buffer = parseInt(currentHostBuffer, 10);
    const reminder = parseInt(currentReminderMins, 10);
    if (Number.isNaN(buffer) || buffer < 0 || buffer > 120) {
      setValidationError('Host buffer must be a whole number between 0 and 120 minutes.');
      return;
    }
    if (Number.isNaN(reminder) || reminder < 0 || reminder > 1440) {
      setValidationError('Reminder window must be a whole number between 0 and 1440 minutes.');
      return;
    }
    if (currentHostMode === 'explicit' && currentHostIds.length === 0) {
      setValidationError(
        'Pick at least one host, or switch to "All guides + facilitators" to send to every staff member on this course.',
      );
      return;
    }
    setValidationError(undefined);

    const hostIdsToSend = currentHostMode === 'explicit' ? currentHostIds : undefined;

    try {
      await store.setCalendarInviteSettings({
        courseId: course.id,
        calendarInvitesEnabled: currentEnabled,
        meetingUrlForInvites: currentMeetingUrl,
        memberInviteDescription: currentMemberDesc,
        hostInviteDescription: currentHostDesc,
        hostBufferMinutes: buffer,
        reminderMinutesBefore: reminder,
        hostUserIds: hostIdsToSend,
      });
    } catch (err) {
      const message = (err as { message?: unknown })?.message;
      if (typeof message === 'string' && message) {
        setValidationError(message);
        return;
      }
      throw err;
    }

    const fresh = await store.getCalendarInviteSettings({ courseId: course.id });
    setSettings(fresh);

    setEnabled(undefined);
    setMeetingUrl(undefined);
    setMemberDesc(undefined);
    setHostDesc(undefined);
    setHostBuffer(undefined);
    setReminderMins(undefined);
    setHostSelectionMode(undefined);
    setExplicitHostIds(undefined);

    showToast({
      title: 'Settings saved',
      message: 'Calendar invite settings have been updated.',
      type: 'ok',
    });
  }

  return (
    <div class="flex flex-col gap-8">
      <AsyncForm class="flex flex-col gap-8" onSubmit={submit}>
        <div class="bg-white rounded-lg border border-gray-200 p-6 flex flex-col gap-5">
          <div class="flex flex-col gap-1">
            <HeadingSecondary>Calendar Invites</HeadingSecondary>
            <Subtext>
              Send members a calendar invite (.ics) automatically on enrollment, and let guides send
              calendar invites for new or rescheduled meetings with one click. Members can RSVP from
              their calendar.
            </Subtext>
          </div>

          <label class="flex items-start gap-3 cursor-pointer">
            <Toggle checked={currentEnabled} onClick={() => setEnabled(!currentEnabled)} />
            <span class="flex flex-col leading-snug">
              <strong>Enable auto calendar invites for this course</strong>
              <span class="opacity-75 text-sm">
                When on, enrolling members receive a welcome email with calendar invites for all
                upcoming meetings.
              </span>
            </span>
          </label>
        </div>

        <div class="bg-white rounded-lg border border-gray-200 p-6 flex flex-col gap-5">
          <HeadingSecondary>Member-facing invite</HeadingSecondary>

          <FormSubSection>
            <InputLabel>Call URL for invites</InputLabel>
            <Subtext>
              The link members will click to join. Goes in the LOCATION field of every invite (e.g.{' '}
              <code>https://mrse.co/aip</code>).
            </Subtext>
            <input
              class="ruz-input"
              type="text"
              value={currentMeetingUrl}
              placeholder="https://mrse.co/your-course"
              onInput={(e) => setMeetingUrl((e.target as HTMLInputElement).value)}
            />
          </FormSubSection>

          <FormSubSection>
            <InputLabel>Member invite description</InputLabel>
            <Subtext>
              Body text shown in the calendar event for members. Plain text — line breaks are
              preserved. This is the default for every meeting; you can override it per meeting.
            </Subtext>
            <textarea
              class="ruz-input"
              rows={6}
              value={currentMemberDesc}
              placeholder={`e.g.\nWelcome to AI Storytelling. Bring a draft to work on live.\n\nJoin link: https://mrse.co/aip`}
              onInput={(e) => setMemberDesc((e.target as HTMLTextAreaElement).value)}
            />
          </FormSubSection>

          <FormSubSection>
            <InputLabel>Reminder window (minutes before)</InputLabel>
            <Subtext>
              How long before the meeting members' calendars should remind them. Default: 10
              minutes.
            </Subtext>
            <input
              class="ruz-input max-w-32"
              type="number"
              min={0}
              max={1440}
              value={currentReminderMins}
              onInput={(e) => setReminderMins((e.target as HTMLInputElement).value)}
            />
          </FormSubSection>
        </div>

        <div class="bg-white rounded-lg border border-gray-200 p-6 flex flex-col gap-5">
          <div class="flex flex-col gap-1">
            <HeadingSecondary>Host-facing invite</HeadingSecondary>
            <Subtext>
              Hosts get a separate calendar invite with a pre-call buffer and any sign-in
              instructions you provide. Visible only to course staff.
            </Subtext>
          </div>

          <FormSubSection>
            <InputLabel>Host invite description</InputLabel>
            <Subtext>
              Body text for the host event — typically the Zoom sign-in URL, alternate host code,
              and any pre-call notes. Plain text.
            </Subtext>
            <textarea
              class="ruz-input"
              rows={6}
              value={currentHostDesc}
              placeholder={`e.g.\nZoom sign-in: https://us02web.zoom.us/s/12345\nAlternate host: ALT-CODE\nBring talking points doc.`}
              onInput={(e) => setHostDesc((e.target as HTMLTextAreaElement).value)}
            />
          </FormSubSection>

          <FormSubSection>
            <InputLabel>Host buffer (minutes)</InputLabel>
            <Subtext>
              Start the host's calendar event this many minutes before the meeting time. Default:
              15.
            </Subtext>
            <input
              class="ruz-input max-w-32"
              type="number"
              min={0}
              max={120}
              value={currentHostBuffer}
              onInput={(e) => setHostBuffer((e.target as HTMLInputElement).value)}
            />
          </FormSubSection>

          <FormSubSection>
            <InputLabel>Who receives the host invite?</InputLabel>
            <Subtext>
              By default, every guide and facilitator on this course gets the host invite. Choose a
              specific subset if needed.
            </Subtext>
            <div class="flex flex-col gap-2 pt-1">
              <label class="flex items-center gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="hostMode"
                  checked={currentHostMode === 'all'}
                  onChange={() => setHostSelectionMode('all')}
                />
                <span>
                  <strong>All guides + facilitators</strong>
                  <span class="opacity-75 text-sm ml-2">
                    (default — picks up new staff automatically)
                  </span>
                </span>
              </label>
              <label class="flex items-center gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="hostMode"
                  checked={currentHostMode === 'explicit'}
                  onChange={() => setHostSelectionMode('explicit')}
                />
                <span>
                  <strong>Choose specific hosts</strong>
                </span>
              </label>
            </div>

            {currentHostMode === 'explicit' && (
              <div class="flex flex-col gap-2 pt-3 pl-7">
                {candidates.length === 0 && (
                  <span class="text-sm opacity-75">
                    No guides or facilitators on this course yet.
                  </span>
                )}
                {candidates.map((u) => {
                  const isSelected = currentHostIds.includes(u.id);
                  return (
                    <label
                      key={u.id}
                      class={`flex gap-3 cursor-pointer items-center border rounded-lg p-2 px-3 ${
                        isSelected ? 'border-indigo-500 ring-1 ring-indigo-500' : ''
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleHost(u.id)}
                      />
                      <UserProfileIcon
                        user={{ name: u.name || u.email, profilePhotoUrl: u.profilePhotoUrl }}
                        size="w-7 h-7"
                      />
                      <span class="flex flex-col leading-snug text-sm">
                        <strong>{u.displayName || u.name || u.email}</strong>
                        <span class="opacity-75">
                          {u.email} · {u.level}
                        </span>
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
          </FormSubSection>
        </div>

        {validationError && (
          <div class="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            {validationError}
          </div>
        )}

        <footer>
          <BtnPrimary>Save</BtnPrimary>
        </footer>
      </AsyncForm>
      {settings.calendarInvitesEnabled && <ResendAllSection courseId={course.id} />}
    </div>
  );
}

function ResendAllSection({ courseId }: { courseId: UUID }) {
  const [resending, setResending] = useState(false);
  const [preview] = useAsyncState(
    () => rpx.courseSettings.getCalendarInviteResendPreview({ courseId }),
    [courseId],
  );
  const [backfillStatus, setBackfillStatus] = useAsyncState(
    () => rpx.courseSettings.getCalendarInviteBackfillStatus({ courseId }),
    [courseId],
  );
  const pollRef = useRef<number | undefined>(undefined);

  // Poll active backfill jobs every 3s while running, stop when finished.
  // Aborted flag guards against in-flight setState calls landing after the
  // effect cleanup (component unmount, course change, or job transition).
  useEffect(() => {
    if (!backfillStatus?.active) {
      return;
    }
    let aborted = false;
    pollRef.current = window.setInterval(async () => {
      const fresh = await rpx.courseSettings.getCalendarInviteBackfillStatus({ courseId });
      if (aborted) {
        return;
      }
      setBackfillStatus(fresh);
      if (!fresh.active) {
        if (pollRef.current !== undefined) {
          window.clearInterval(pollRef.current);
          pollRef.current = undefined;
        }
        if (fresh.latest?.status === 'completed') {
          showToast({
            type: fresh.latest.sendsFailed > 0 ? 'warn' : 'ok',
            title: 'Backfill complete',
            message: `${fresh.latest.sendsSucceeded} invite${fresh.latest.sendsSucceeded === 1 ? '' : 's'} delivered across ${fresh.latest.meetingsProcessed} meeting${fresh.latest.meetingsProcessed === 1 ? '' : 's'}${fresh.latest.sendsFailed > 0 ? ` · ${fresh.latest.sendsFailed} failed` : ''}.`,
          });
        } else if (fresh.latest?.status === 'failed') {
          showToast({
            type: 'warn',
            title: 'Backfill failed',
            message: fresh.latest.errorMessage ?? 'Check Render logs for details.',
          });
        }
      }
    }, 3000);
    return () => {
      aborted = true;
      if (pollRef.current !== undefined) {
        window.clearInterval(pollRef.current);
        pollRef.current = undefined;
      }
    };
  }, [backfillStatus?.active?.id, courseId]);

  async function send() {
    if (resending || !preview || backfillStatus?.active) {
      return;
    }
    const meetingWord = preview.meetingCount === 1 ? 'meeting' : 'meetings';
    const memberWord = preview.memberCount === 1 ? 'member' : 'members';
    const isLargeJob = preview.meetingCount > preview.cap;

    const confirmed = await showDialog({
      mode: 'warn',
      title: isLargeJob ? 'Run backfill in the background?' : 'Resend calendar invites?',
      children: isLargeJob
        ? `This course has ${preview.meetingCount} upcoming ${meetingWord} — too many to send synchronously. The backfill job will run in the background, sending updated calendar invites (.ics) to ${preview.memberCount} ${memberWord} for each meeting (~${preview.estimatedSends} invite emails total). Members may see this as event updates on their calendar.`
        : `This will send an updated calendar invite (.ics) to ${preview.memberCount} ${memberWord} for each of ${preview.meetingCount} upcoming ${meetingWord} (~${preview.estimatedSends} invite emails). Members may see this as an event update on their calendar.`,
      confirmButtonText: isLargeJob ? 'Start backfill' : 'Resend invites',
    });
    if (!confirmed) {
      return;
    }
    setResending(true);
    try {
      if (isLargeJob) {
        await rpx.courseSettings.startCalendarInviteBackfill({ courseId });
        const fresh = await rpx.courseSettings.getCalendarInviteBackfillStatus({ courseId });
        setBackfillStatus(fresh);
        showToast({
          type: 'ok',
          title: 'Backfill queued',
          message: `Running in the background — check back here for progress (updates every few seconds).`,
        });
      } else {
        const result = await rpx.courseSettings.resendAllCalendarInvites({ courseId });
        const meetingsLabel = result.meetingsTriggered === 1 ? 'meeting' : 'meetings';
        const sendsLabel = result.sendsSucceeded === 1 ? 'invite' : 'invites';
        const detailParts: string[] = [];
        if (result.sendsSkipped > 0) {
          detailParts.push(`${result.sendsSkipped} skipped by allowlist`);
        }
        if (result.sendsFailed > 0) {
          detailParts.push(`${result.sendsFailed} failed — check Render logs`);
        }
        const detail = detailParts.length > 0 ? ` · ${detailParts.join(' · ')}` : '';
        showToast({
          type: result.sendsFailed > 0 ? 'warn' : 'ok',
          title: 'Invites resent',
          message: `Triggered ${result.meetingsTriggered} ${meetingsLabel} · ${result.sendsSucceeded} ${sendsLabel} delivered${detail}.`,
        });
      }
    } catch (err) {
      showError(err);
    } finally {
      setResending(false);
    }
  }

  const activeJob = backfillStatus?.active;
  const isLargeJob = preview && preview.meetingCount > preview.cap;

  return (
    <div class="bg-white rounded-lg border border-gray-200 p-6 flex flex-col gap-4">
      <div class="flex flex-col gap-1">
        <HeadingSecondary>Send invites for all upcoming meetings</HeadingSecondary>
        <Subtext>
          Pushes updated calendar invites to every enrolled member for every upcoming meeting on
          this course. Use for the initial cutover from manual calendar setup, or after editing
          course-level descriptions, member URL, or host list above.
        </Subtext>
        {preview && (
          <Subtext>
            {preview.meetingCount === 0 ? (
              <>No upcoming meetings to send.</>
            ) : (
              <>
                {preview.meetingCount} upcoming meeting
                {preview.meetingCount === 1 ? '' : 's'} × {preview.memberCount} member
                {preview.memberCount === 1 ? '' : 's'} = ~{preview.estimatedSends} invite email
                {preview.estimatedSends === 1 ? '' : 's'}
                {isLargeJob ? ' (will run as a background job)' : ''}.
              </>
            )}
          </Subtext>
        )}
      </div>
      {activeJob ? (
        <BackfillProgress job={activeJob} />
      ) : (
        <div>
          <BtnSecondary
            disabled={resending || !preview || preview.meetingCount === 0}
            onClick={send}
          >
            {resending ? 'Sending…' : isLargeJob ? 'Start backfill' : 'Send invites'}
          </BtnSecondary>
        </div>
      )}
    </div>
  );
}

function BackfillProgress({
  job,
}: {
  job: {
    meetingsProcessed: number;
    totalMeetings: number;
    sendsSucceeded: number;
    sendsSkipped: number;
    sendsFailed: number;
    status: 'pending' | 'running' | 'completed' | 'failed';
  };
}) {
  const pct =
    job.totalMeetings === 0 ? 0 : Math.round((job.meetingsProcessed / job.totalMeetings) * 100);
  const statusLabel = job.status === 'pending' ? 'Queued — starting within ~2 minutes' : 'Running';
  return (
    <div class="flex flex-col gap-2">
      <div class="flex items-center gap-3 text-sm">
        <Spinner class="border-sky-500 w-4 h-4" />
        <span>
          <strong>{statusLabel}</strong> · {job.meetingsProcessed} / {job.totalMeetings} meetings
          processed · {job.sendsSucceeded} invite{job.sendsSucceeded === 1 ? '' : 's'} delivered
          {job.sendsFailed > 0 ? ` · ${job.sendsFailed} failed` : ''}
        </span>
      </div>
      <div class="h-2 w-full bg-gray-100 rounded-full overflow-hidden">
        <div class="h-full bg-sky-500 transition-all duration-500" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}
